import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import { toAuditEntries } from '../adapters';

import {
  fileToDataUrl, REWARD_TYPE_NOTES, REWARD_UNSUPPORTED_FIELDS, toCrmRewardPatch, toUiReward,
} from '../adapters';
import { RewardCatalogScreen, type PackageOption } from '../../screens/RewardCatalogScreen';
import type { Reward } from '../../screens/loyaltyData';
import type { PackageSummary } from '../coreClient';
import type { AuditLogEntry } from '../crmClient';

const toPackageOption = (p: PackageSummary): PackageOption => ({
  id: p.id,
  code: p.code,
  name: p.name,
  imageUrl: p.imageHero,
  thumbnailUrl: p.imageThumbnail,
});

/**
 * ดึงภาพของแพ็กเกจมาเป็น data URI เพื่อส่งให้ backend เก็บเป็นภาพของของรางวัลเอง
 *
 * ⚠️ ส่ง URL ไปตรง ๆ ไม่ได้ — ช่องภาพของ backend ตีความ URL ว่า "คงภาพเดิมไว้"
 *    ของรางวัลใหม่จึงจะไม่มีภาพเลย
 * ภาพอยู่บน storage ที่อนุญาต CORS ให้ host อยู่แล้ว (ตรวจบน staging แล้ว)
 */
async function packageImageToDataUrl(url: string): Promise<string> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    return await fileToDataUrl(await res.blob());
  } catch {
    throw new Error('ดึงภาพจากแพ็กเกจไม่สำเร็จ — อัปโหลดภาพเองแล้วบันทึกอีกครั้ง หรือเอาภาพออกก่อนบันทึก');
  }
}

/**
 * ต่อแคตตาล็อกของรางวัลเข้ากับ CRM module (FR-ADM-22)
 *
 * CRM เก็บ ประเภท · คะแนน · ช่วงเวลา · เงื่อนไขการใช้ · การเปิด/ปิด · มูลค่าและอายุคูปองได้แล้ว
 * (คูปองสร้างเป็น VoucherTemplate ของ BIL ให้อัตโนมัติ ไม่ต้องเรียก BIL จากที่นี่)
 *
 * ⚠️ ที่ยังไม่รองรับอยู่ใน REWARD_UNSUPPORTED_FIELDS — หน้าจอซ่อนส่วนเหล่านั้นให้เอง
 *    เพราะช่องที่กรอกได้แต่บันทึกไม่ขึ้นคือกับดักที่ร้ายกว่าการไม่มีช่อง
 * ⚠️ ประเภทเปลี่ยนไม่ได้หลังสร้าง (backend ตอบ 400) จึงส่ง lockTypeOnEdit
 * ⚠️ ปุ่มลบ **ลบจริง** — การหยุดให้แลกชั่วคราวใช้ช่อง "เปิดให้แลกในแอป" (is_active)
 *
 * ⚠️ **การเขียนต้องเป็นสมาชิกของ division**
 *    ถ้าบัญชีไม่ได้เป็นสมาชิกของ division จะอ่านได้แต่บันทึกไม่ได้ ตอบ 403
 *    ข้อความจาก backend ถูกส่งต่อไปแสดงใน toast ตรง ๆ ไม่กลืนทิ้ง
 */
export function RewardCatalogContainer() {
  const { api, dataMode, divisionId } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [rewards, setRewards] = useState<Reward[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  /**
   * ประวัติการแก้ของรางวัล — ของเสริม ล้มแล้วหน้ายังใช้งานได้
   * เก็บแถวดิบไว้ แล้วแปลงทีหลัง เพื่อให้ชื่อแพ็กเกจขึ้นได้แม้รายการแพ็กเกจมาทีหลัง
   */
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[] | undefined>(undefined);
  /**
   * แพ็กเกจที่เลือกเป็นของรางวัลประเภทสินค้าได้
   * ⚠️ ดึงไม่ได้ให้เป็นรายการว่าง ไม่ใช่ undefined — undefined แปลว่าโหมดสาธิต
   *    ซึ่งจะกลับไปใช้ช่องสต๊อกตามงานออกแบบเดิมแล้วบันทึกไม่ผ่าน
   */
  const [packages, setPackages] = useState<PackageSummary[]>([]);
  /** แพ็กเกจมีมากกว่าที่ดึงมาครั้งเดียว — ต้องค้นที่ server */
  const [packagesTruncated, setPackagesTruncated] = useState(false);

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');
    Promise.all([
      api.listRewards(),
      /* ประวัติเป็นของเสริม ล้มแล้วไม่ควรทำให้แคตตาล็อกใช้ไม่ได้ */
      api.listAuditLogs('reward').catch(() => null),
      /*
       * แพ็กเกจใช้เฉพาะตอนตั้งของรางวัลประเภทสินค้า — ล้มแล้วยังดูแคตตาล็อกได้
       * ⚠️ ห่อด้วย Promise.resolve().then — host ที่ deploy แยกจากโมดูลอาจยังไม่มีเมธอดนี้
       *    เรียกตรง ๆ จะ throw ทันทีก่อนถึง .catch แล้วทั้งหน้าขาว
       */
      Promise.resolve().then(() => api.listPackages(divisionId)).catch(() => null),
    ])
      .then(([page, audit, pkgs]) => {
        if (cancelled) return;
        setRewards(page.items.map(toUiReward));
        setPackages(pkgs?.items ?? []);
        setPackagesTruncated(Boolean(pkgs && pkgs.total > pkgs.items.length));
        setAuditLogs(audit?.items);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  /**
   * บันทึกของรางวัลหนึ่งรายการ
   *
   * id ของรายการใหม่ที่หน้าจอสร้างเป็น `rw-<timestamp>` ซึ่งไม่ใช่เลข
   * ใช้ตรงนี้แยกว่าเป็นการสร้างใหม่หรือแก้ของเดิม
   */
  const onSave = useCallback(async (r: Reward, changeReason?: string) => {
    const id = Number(r.id);
    const isNew = !(Number.isInteger(id) && id > 0);
    const patch = { ...toCrmRewardPatch(r, isNew), changeReason };

    /**
     * รูปเดิมถูกเอาออก — ล้างด้วย PATCH ปกติ ไม่ต้องใช้ multipart
     * ดูจากรายการที่โหลดมา ไม่ใช่จากค่าที่หน้าจอส่งมา เพราะหน้าจอไม่รู้ว่าของเดิมมีรูปไหม
     */
    const had = rewards?.find((x) => x.id === r.id)?.imageUrl;
    if (!isNew && had && !r.imageUrl) patch.image = null;

    /* รูปใหม่ไปกับคำขอเดียวกัน — ไม่มีจังหวะที่ข้อมูลบันทึกแล้วแต่รูปยังไม่ขึ้น */
    if (r.imageFile) patch.image = await fileToDataUrl(r.imageFile);
    else if (r.imageFromPackage !== undefined && r.imageUrl) patch.image = await packageImageToDataUrl(r.imageUrl);

    if (isNew) await api.createReward({ ...patch, division: divisionId });
    else await api.updateReward(id, patch);

    const [fresh, audit] = await Promise.all([
      api.listRewards(),
      /* ดึงประวัติใหม่ให้เห็นรายการที่เพิ่งบันทึกพร้อมเหตุผล */
      api.listAuditLogs('reward').catch(() => null),
    ]);
    setRewards(fresh.items.map(toUiReward));
    if (audit) setAuditLogs(audit.items);
  }, [api, divisionId, rewards]);

  const packageOptions = useMemo(() => packages.map(toPackageOption), [packages]);

  const auditEntries = useMemo(() => {
    if (!auditLogs) return undefined;
    const nameById = new Map(packages.map((p) => [p.id, p.name]));
    return toAuditEntries(auditLogs, { packageName: (id) => nameById.get(id) });
  }, [auditLogs, packages]);

  const searchPackages = useCallback(
    (keyword: string) => api.listPackages(divisionId, { keyword }).then((r) => r.items.map(toPackageOption)),
    [api, divisionId],
  );

  const onDelete = useCallback(async (r: Reward) => {
    const id = Number(r.id);
    if (!Number.isInteger(id) || id <= 0) return;
    await api.deleteReward(id);
    const fresh = await api.listRewards();
    setRewards(fresh.items.map(toUiReward));
  }, []);

  if (isMock) return <RewardCatalogScreen initialState="ready" />;

  return (
    <RewardCatalogScreen
      initialState={state}
      rewards={rewards ?? []}
      onSave={onSave}
      onDelete={onDelete}
      onRefresh={load}
      refreshing={state === 'loading'}
      unsupportedFields={REWARD_UNSUPPORTED_FIELDS}
      lockTypeOnEdit
      typeNotes={REWARD_TYPE_NOTES}
      packageOptions={packageOptions}
      searchPackages={packagesTruncated ? searchPackages : undefined}
      auditEntries={auditEntries ?? []}
    />
  );
}
